import * as React from 'react';

import isEmpty from 'lodash/isEmpty';

import requestCountry from 'js/lib/requestCountry';
import { useLocation, useParams } from 'js/lib/useRouter';
import { isPoweruser } from 'js/lib/user';

import { NotFound as NotFoundPage } from 'bundles/common/components/withNotFound';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import DegreesDiscoveryEpic from 'bundles/epic/clients/DegreesDiscovery';
import { AVAILABILITY_STATUS } from 'bundles/premium-hub/types';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';
import DegreeNotAvailable from 'bundles/xddp/components/DegreeNotAvailable';

type Props = {
  children?: JSX.Element;
};

export const NotFound = () => {
  // If on server, serve a true 404 (edge will handle rendering 404 page).
  if (inServerContext) {
    throw new NotFoundError();
  } else {
    return <NotFoundPage />;
  }
};

/**
 * Our edge routes are configured so that only Courserians have access to the `/degree-description/` route. If you're testing a degree page on that path, you're not required to sign in to test a degree program that isn't LIVE. Otherwise, you have to be signed into your Coursera account to see a PRE_LAUNCH degree, for example.
 * @param pathname A string that represents the pathname, e.g. /degrees/ or /degree-description/imba
 * @returns boolean
 */
const isOnDegreeDescriptionPath = (pathname: string) => {
  const degreeDescriptionRegex = /^\/degree-description\//g;
  const matches = pathname.match(degreeDescriptionRegex);

  if (matches && matches.length > 0) {
    return true;
  } else {
    return false;
  }
};

const isDegreeAvailable = (degreeAvailabilityStatus: string, pathname: string) => {
  const pathIsDegreeDescription = isOnDegreeDescriptionPath(pathname);

  const isLive = degreeAvailabilityStatus === AVAILABILITY_STATUS.LIVE;
  const isPreLaunch = degreeAvailabilityStatus === AVAILABILITY_STATUS.PRE_LAUNCH;
  const hasSpecialPermissions = isPoweruser() || pathIsDegreeDescription;

  return isLive || (hasSpecialPermissions && isPreLaunch);
};

const isDegreeBlockedInCurrentRegion = (blockedCountries?: Array<string>) =>
  blockedCountries?.includes(requestCountry.get());

export const isSlugValid = (slug?: string) => !!slug && /^[a-zA-Z-]*$/.test(slug);

/**
 * 1. If the page slug is only a valid string, as in [A-Za-z] plus dashes, to avoid folks putting in https://www.coursera.org/degrees/imba//%5c..%5c..%5c..%5c../etc/passwd to try to hack us like here: https://phabricator.dkandu.me/D112331
 * 2. If degree actually exists in our backend (in PremiumProductCollections) and is a LIVE degree
 * 3  If user is a poweruser, also show TEST or PRE_LAUNCH degrees too
 * 4. If slug in Contentful entry slug matches the slug in page URL
 */
const ConsumerPageGuard = ({ children }: Props) => {
  const { blockedCountries, degreeAvailabilityStatus, loading, id, pageContent } = useDegreeDescriptionPageData();
  const { slug } = useParams();
  const location = useLocation();

  const contentLoaded = !loading && !isEmpty(pageContent);
  const degreeIsAvailable = isDegreeAvailable(degreeAvailabilityStatus, location.pathname);

  const throttledDegreeList = DegreesDiscoveryEpic.get('throttledDestinationDegreeList');

  /**
   * This is a list of degrees that we are throttling traffic from discovery surface areas (search, carousels, etc).
   * This list is coordinated with marketing. Throttled degrees have a a "prelaunch" status and may not be marked as live.
   *
   * When we archive degrees in this list or delete them, we need to remove them from the EPIC experiment as well.
   * https://tools.coursera.org/epic/experiment/0OYyGASgEe-C1w6tOivQcQ
   */
  const isThrottledDegree =
    throttledDegreeList &&
    throttledDegreeList.length > 0 &&
    throttledDegreeList.filter((degree: { id: string; slug: string }) => degree?.slug === slug).length > 0;

  const shouldShowDegree = (isSlugValid(slug) && degreeIsAvailable && !!id) || isThrottledDegree;

  if (loading) {
    // TODO: Render more intelligent-looking page skeleton here?
    return null;
  } else if (contentLoaded && !shouldShowDegree) {
    return <NotFound />;
  } else if (isDegreeBlockedInCurrentRegion(blockedCountries)) {
    return <DegreeNotAvailable />;
  } else {
    return children ?? null;
  }
};

export default ConsumerPageGuard;
